import figure1 from '@/assets/image/blog/blogDetail2/figure_1.png'
import figure2 from '@/assets/image/blog/blogDetail2/figure_2.png'
import figure3 from '@/assets/image/blog/blogDetail2/figure_3.png'
import table1 from '@/assets/image/blog/blogDetail2/table_1.png'
import figure4 from '@/assets/image/blog/blogDetail2/figure_4.png'

const executionVerificationContent = [
  {
    paragraphs: [
      'Oracles verified data for DeFi. Agent finance needs something different.',
      'It needs a system that verifies actions before capital moves.',
      'That system is the **invariant layer**.',
      'When capital becomes autonomous, signatures are no longer enough. Signatures prove authorization. They do not prove correctness.',
      'Financial systems were designed to verify who authorized a transaction, not whether the transaction itself is correct. That assumption worked when humans executed every action manually.',
      'But AI agents are now executing financial transactions autonomously: trading across DeFi pools, routing payments, and managing treasury operations without human intervention.',
      'Once execution becomes autonomous, authorization is no longer the core problem. Execution itself must be verified, and correctness becomes a cryptographic property.',
      'Blockchains verify computation.\nOracles verify data.\nVerification layers verify execution.',
      'Yet today, no infrastructure exists to verify whether a transaction still satisfies the constraints originally approved by the user.',
      'Most agent transactions execute with nothing more than a signature proving the agent was authorized to act.',
    ],
  },
  {
    heading: 'Where This Problem Appears First',
    paragraphs: [
      'One of the first environments where this gap becomes visible is prediction markets.',
      'Prediction markets are increasingly executed by automated systems: agents discovering signals, allocating capital across markets, and executing trades without human intervention.',
      'But these systems still rely on signatures to authorize execution, not infrastructure to verify whether the execution still respects the constraints originally approved by the user.',
      '**Authorized is not the same as correct.**',
      'The gap between those two properties is where funds get drained, compliance breaks down, and institutions refuse to onboard.',
      'This missing layer is what we call execution verification.',
      'Vishwa is building the execution verification layer for autonomous capital: infrastructure that sits between agent decision systems and financial execution venues.',
      'Before capital moves, the system verifies that the transaction satisfies the invariant constraints originally approved by the user or institution.',
      'In the agent economy, signatures stop being the mechanism that governs capital movement.',
      '**Invariants replace them.**',
      'Those constraints form an invariant layer, defining when capital is allowed to move, regardless of whether verification is enforced through:',
    ],
    points: ['**Zero-knowledge proofs (ZK)**', '**Trusted execution environments (TEE)**'],
    figure: {
      src: figure1,
      alt: 'Verification Gap',
      caption:
        'Figure 1: Verification Gap. Most agents today only prove authorization. Verifiable Agent Execution Bundle (VAEB) verifies the transaction itself via ZK proofs or TEE attestation, before it hits the chain.',
    },
  },
  {
    paragraphs: [
      'As AI agents start moving real money, whether in trading, payments, or treasury operations, the financial system needs a way to enforce execution constraints when nobody manually approves transactions anymore.',
      'Vishwa solves this by cryptographically verifying execution before it happens: authorization, solvency, and execution integrity.',
      'If any proof fails, the transaction simply cannot execute.',
      'Today, roughly **$50M** of capital has already executed through verification-gated flows in the system, with around **$750M** structured to require this infrastructure.',
      '**Verifiable Agent Execution Bundle (VAEB)** is the core of that system. It sits between the agent and the blockchain, intercepts the agent’s transaction, verifies it against the user’s original constraints, and only then submits it on-chain.',
    ],
    figure: {
      src: figure2,
      alt: 'Verification layer execution path',
      caption: 'Figure 2. Verification Layer is mandatory for trustworthy execution path of agent transactions',
    },
  },
  {
    heading: 'The Right Question: Verify the Transaction, Not the Agent',
    paragraphs: [
      'A natural instinct when thinking about autonomous systems is to verify the agent itself: audit its code, inspect its reasoning, or prove that the model behaves correctly.',
      'But this approach quickly breaks down in real deployments.',
      'Modern agents are frontier language models running in external clouds, wrapped in orchestration layers, and translating natural language into financial transactions across multiple systems. No single observer can fully inspect or formally verify that entire stack.',
      'Instead, the practical question becomes much simpler:',
      '**Does this transaction match what the user asked for?**',
      'Verification must live as infrastructure in the execution path.',
      'Autonomous execution increasingly spans multiple venues: blockchains, exchanges, custodians, and payment systems. The verification layer must sit between agent decision systems and execution venues, ensuring that every transaction satisfies its invariant constraints before capital moves.',
      'When a user tells an agent, “pay 25 USDC to Alice,” they define a set of constraints:',
    ],
    points: ['the recipient', 'the amount', 'the token', 'the expiry window'],
    tailParagraphs: [
      'These constraints form the invariant layer that defines when capital is allowed to move.',
      'Verification then becomes a tractable problem: confirming that the transaction respects every constraint before it hits the chain.',
    ],
    figure: {
      src: figure3,
      alt: 'Two Verification Paths',
      caption:
        'Figure 3: Two Verification Paths. Two verification paths, one outcome. Both start from the same user intent and converge at on-chain execution. The ZK path provides mathematical certainty; the TEE path provides hardware-attested speed.',
    },
  },
  {
    heading: 'Path 1: ZK Proofs, for When the Proof Is the Product',
    paragraphs: [
      'Zero-knowledge proofs encode the user’s constraints as a mathematical circuit. When the agent produces a transaction, a prover generates a cryptographic proof that the transaction satisfies every constraint. The on-chain contract checks the proof. If it passes, the transaction executes. If not, it is rejected.',
      'No hardware dependency.\nNo infrastructure assumption.\nThe proof is self-contained.',
      '**Where this ships**',
      'Institutional custody and settlement. When a regulated fund is moving assets on-chain, its compliance team does not care about milliseconds. It cares about audit trails that hold up under regulatory scrutiny, privacy preservation that keeps business secrets intact, and cross-validation of on-chain behavior. A ZK proof is a mathematically verifiable receipt that the transaction was correct. For those operators, that is a prerequisite.',
      '**The tradeoff**',
      'Proof generation takes roughly two seconds, and can take longer for more complex behavior. For a weekly settlement, that is negligible. For a strategy executing hundreds of trades per hour, it is not.',
    ],
  },
  {
    heading: 'Path 2: TEE Verification, for When Speed Is the Constraint',
    paragraphs: [
      'A Trusted Execution Environment (TEE) is a hardware-isolated secure enclave built into modern processors, including Intel TDX, AMD SEV-SNP, and ARM TrustZone. Instead of encoding constraints as a circuit, the system encodes them as a lightweight program that runs inside the enclave.',
      'The TEE checks the transaction against the user’s constraints and produces a signed attestation: a hardware-backed certificate proving which program ran, what it checked, and that every constraint passed. The smart contract verifies the signature and executes. Verification takes less than 10 milliseconds. On-chain cost drops from around 200,000 gas to around 5,000 gas.',
      '**Where this ships:**',
      'High-frequency execution. A yield optimizer rebalancing across fifteen pools, an arbitrage agent operating in sub-second windows, or a liquidation bot that needs to verify and execute before the opportunity disappears all require verified execution at market speed. Two seconds kills the trade. Ten milliseconds does not.',
      '**The tradeoff**',
      'You are trusting the hardware manufacturer and attestation infrastructure rather than pure mathematics. For operators in this category, that is an easy trade. AWS, Azure, and Google Cloud have already built entire confidential computing product lines around these same trust assumptions.',
    ],
    figure: {
      src: table1,
      alt: 'ZKP and TEE application scenarios',
      caption: 'Table 1. ZKP & TEE apply to different scenarios.',
    },
  },
  {
    heading: 'Why Both, and Why Now',
    paragraphs: [
      'The verification market for autonomous capital is not a single market.',
      'It is at least four, and each segment has fundamentally different requirements around latency, privacy, and trust assumptions.',
    ],
    figure: {
      src: figure4,
      alt: 'Market Positioning Matrix',
      caption:
        'Figure 4. Market Positioning Matrix. The agent execution market spans four segments with different speed and assurance requirements. A single verification path leaves half the market unserved.',
    },
  },
  {
    heading: '1. Regulated institutions',
    paragraphs: [
      'Regulated institutions need mathematical proofs for compliance and privacy.',
      'Their legal and compliance teams want verification artifacts that are self-contained, privacy-preserving, and auditable.',
      'This is table stakes for institutional DeFi adoption.',
      '→ ZK path',
    ],
  },
  {
    heading: '2. High-frequency autonomous execution',
    paragraphs: [
      'Automated high-frequency strategies need speed.',
      'One of the core value propositions of autonomous agents is that they can operate faster than humans, around the clock. If verification adds seconds of latency to every transaction, the agent can become slower than a person clicking buttons on a DEX.',
      'TEE verification makes the speed promise real.',
      '→ TEE path',
    ],
  },
  {
    heading: '3. Traditional finance moving on-chain',
    paragraphs: [
      'Banks piloting tokenized treasuries, brokerages testing on-chain FX, and institutions exploring syndicated loan infrastructure may find both routes intuitive.',
      'These institutions already operate within hardware security module trust models, so TEE attestation maps naturally onto frameworks they have used for decades. At the same time, they also care about privacy preservation and execution-path validation.',
      '→ Both paths',
    ],
  },
  {
    heading: '4. Cross-chain operators',
    paragraphs: [
      'Cross-chain operators routing capital between networks can use a tiered model: fast TEE attestation for routine transfers, and ZK proofs when transaction value warrants a stronger guarantee.',
      'Verification scales with the stakes.',
      'This mirrors the security models traditional financial systems already use. Operators do not run the same checks on a $50 wire and a $50M settlement.',
      '→ Both paths',
    ],
  },
  {
    heading: 'The Bigger Picture',
    paragraphs: [
      'A verification layer that serves only one of these markets leaves the rest untouched.',
      'That is why VAEB ships both paths.',
      'The agent economy is forming now.',
      'Not next year.\nNot after the next bull run.\nRight now.',
      'Agents are already executing real transactions with real capital.',
      'Most of them are running unverified.',
      'The gap between “this agent is authorized” and “this transaction is correct” is where the next wave of exploits, compliance failures, and institutional hesitancy lives.',
      'What signatures were to the internet economy, invariants will be to the agent economy.',
      'They define when capital is allowed to move.',
      'And the systems that enforce them will become the verification layer of autonomous finance.',
      'That is the role of execution verification.',
    ],
  },
  {
    heading: 'About Vishwa',
    paragraphs: [
      'Vishwa is building the execution verification layer for agent-driven capital: infrastructure that verifies whether transactions are allowed to execute before capital moves.',
      'As AI agents begin executing financial transactions across trading, payments, and treasury operations, systems need a way to ensure that execution still matches user intent.',
      'Vishwa verifies transactions before execution by enforcing three core invariants:',
    ],
    points: [
      '**Authorization:** the agent is permitted to act',
      '**Solvency:** sufficient capital exists',
      '**Execution integrity:** the transaction satisfies the defined constraints',
    ],
    tailParagraphs: ['If any invariant fails, the transaction cannot execute.'],
  },
  {
    heading: 'Traction',
    paragraphs: [
      'Around $50M in capital has already run through verification-gated flows in the system.',
      'Around $750M in institutional capital is structured to require verified execution infrastructure.',
    ],
  },
  {
    heading: 'Strategic partners',
    paragraphs: ['Metalpha, Fidelity Labs, Anchorage, Ceffu, and BTOG'],
  },
  {
    heading: 'Strategic investors',
    paragraphs: [
      'Dispersion and Panga Capital, alongside individuals across Fidelity Labs, Salesforce, Mysten Labs, Avalanche, Coinbase, and others.',
    ],
  },
  {
    heading: 'Learn more',
    link: 'https://www.vishwalab.com/',
    linkName: 'vishwalab.com'
  },
]

export default executionVerificationContent
